import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface AuditRow {
  readonly auditId: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  /** Job the row concerns — its own, or its review's. */
  readonly jobNumber: string | null;
  readonly planCode: string | null;
  readonly planName: string | null;
  readonly actionCode: string;
  readonly entityType: string;
  readonly beforeState: Record<string, unknown> | null;
  readonly afterState: Record<string, unknown> | null;
  readonly delta: Record<string, unknown> | null;
  readonly prevHash: string | null;
  readonly currHash: string;
  readonly ipAddress: string | null;
  readonly createdAt: string;
}

export function ShAuditTrailPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  useEffect(() => {
    let alive = true;
    const qs = new URLSearchParams();
    qs.set('offset', String(offset));
    qs.set('limit', String(limit));
    if (filterEntity) qs.set('entityType', filterEntity);
    if (filterAction) qs.set('actionCode', filterAction);

    apiRequest<AuditRow[]>(`/v1/sh/audit?${qs.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, offset, filterEntity, filterAction]);

  return (
    <main aria-label="sh-audit-trail">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/sh/dashboard" data-testid="back-dashboard">{t('sh.common.dashboard')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.audit.title')}</h1>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}

      {/* ── Filters ──────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <label htmlFor="aud-entity" style={{ fontSize: 11 }}>{t('sh.audit.entityType')}</label>
          <select id="aud-entity" value={filterEntity} onChange={(e) => { setFilterEntity(e.target.value); setOffset(0); }}
                  data-testid="filter-entity"
                  style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)', marginLeft: 4 }}>
            <option value="">{t('sh.audit.all')}</option>
            <option value="REVIEW">{t('sh.audit.entReview')}</option>
            <option value="BUDGET_PLAN">{t('sh.audit.entBudgetPlan')}</option>
            <option value="ALLOCATION">{t('sh.audit.entAllocation')}</option>
            <option value="CONSUMPTION">{t('sh.audit.entConsumption')}</option>
            <option value="EXCEPTION">{t('sh.audit.entException')}</option>
            <option value="ESCALATION">{t('sh.audit.entEscalation')}</option>
            <option value="CLARIFICATION">{t('sh.audit.entClarification')}</option>
            <option value="DELEGATION">{t('sh.audit.entDelegation')}</option>
            <option value="ROI">{t('sh.audit.entRoi')}</option>
            <option value="APPROVAL">{t('sh.audit.entApproval')}</option>
          </select>
        </div>
        <div>
          <label htmlFor="aud-action" style={{ fontSize: 11 }}>{t('sh.audit.action')}</label>
          <input id="aud-action" value={filterAction} onChange={(e) => { setFilterAction(e.target.value); setOffset(0); }}
                 data-testid="filter-action" placeholder={t('sh.audit.actionPlaceholder')}
                 style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)', marginLeft: 4 }} />
        </div>
      </div>

      {/* ── Table ────────────────────────────────────── */}
      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.audit.empty')}</p>
      ) : (
        <>
          <table aria-label="audit-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px' }}>{t('sh.audit.action')}</th>
                <th style={{ padding: '8px 4px' }}>{t('sh.audit.colEntity')}</th>
                <th style={{ padding: '8px 4px' }}>{t('sh.audit.colReference')}</th>
                <th style={{ padding: '8px 4px' }}>{t('sh.audit.colActor')}</th>
                <th style={{ padding: '8px 4px' }}>{t('sh.audit.colIp')}</th>
                <th style={{ padding: '8px 4px' }}>{t('sh.audit.colTime')}</th>
                <th style={{ padding: '8px 4px' }}>{t('sh.audit.colHash')}</th>
                <th style={{ padding: '8px 4px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.auditId}>
                  <tr style={{ borderBottom: '1px solid var(--color-border-default)', cursor: 'pointer' }}
                      onClick={() => setExpandedId(expandedId === r.auditId ? null : r.auditId)}>
                    <td style={{ padding: '8px 4px', fontSize: 12 }}>{r.actionCode}</td>
                    <td style={{ padding: '8px 4px' }}>{r.entityType}</td>
                    <td style={{ padding: '8px 4px', fontSize: 12 }}>{r.jobNumber ?? r.planCode ?? '—'}</td>
                    <td style={{ padding: '8px 4px', fontSize: 12 }}>{personLabel(r.actorName, r.actorCode) ?? '—'}</td>
                    <td style={{ padding: '8px 4px', fontSize: 11 }}>{r.ipAddress ?? '—'}</td>
                    <td style={{ padding: '8px 4px', fontSize: 11 }}>{at(r.createdAt)}</td>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace', fontSize: 10, color: 'var(--color-text-muted)' }}>
                      {r.currHash.slice(0, 12)}…
                    </td>
                    <td style={{ padding: '8px 4px', fontSize: 12 }}>{expandedId === r.auditId ? '▼' : '▶'}</td>
                  </tr>
                  {expandedId === r.auditId && (
                    <tr>
                      <td colSpan={8} style={{ padding: 12, background: 'var(--color-bg-subtle)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <strong style={{ fontSize: 12 }}>{t('sh.audit.before')}</strong>
                            <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200, background: 'var(--color-bg-surface)', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                              {r.beforeState ? JSON.stringify(r.beforeState, null, 2) : t('sh.audit.none')}
                            </pre>
                          </div>
                          <div>
                            <strong style={{ fontSize: 12 }}>{t('sh.audit.after')}</strong>
                            <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200, background: 'var(--color-bg-surface)', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                              {r.afterState ? JSON.stringify(r.afterState, null, 2) : t('sh.audit.none')}
                            </pre>
                          </div>
                        </div>
                        {r.delta && (
                          <div style={{ marginTop: 8 }}>
                            <strong style={{ fontSize: 12 }}>{t('sh.audit.delta')}</strong>
                            <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 150, background: 'var(--color-bg-surface)', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                              {JSON.stringify(r.delta, null, 2)}
                            </pre>
                          </div>
                        )}
                        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
                          <div>{t('sh.audit.linkage', { plan: r.planName ?? '—', job: r.jobNumber ?? '—' })}</div>
                          <div>{t('sh.audit.prevHash', { hash: r.prevHash ?? t('sh.audit.genesis') })}</div>
                          <div>{t('sh.audit.currHash', { hash: r.currHash })}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          {/* ── Pagination ───────────────────────────── */}
          <nav aria-label="audit-pagination" style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button type="button" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
                    data-testid="btn-prev">{t('sh.audit.prev')}</Button>
            <span style={{ fontSize: 12 }}>{t('sh.audit.showing', { from: offset + 1, to: offset + rows.length })}</span>
            <Button type="button" variant="secondary" disabled={rows.length < limit} onClick={() => setOffset(offset + limit)}
                    data-testid="btn-next">{t('sh.audit.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
